import * as audioOps from '../../domain/audioOps';
import { INSTRUMENT_LABELS } from '../../domain/factories';
import { CHOKE_GROUPS, DRUM_INSTRUMENTS, type Instrument, type SampleSlot } from '../../domain/schemas';
import { useObservable } from '../../lib/useObservable';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { useOpRunner } from '../music/useOpRunner';
import { Alert, Button, Panel } from '../ui';

const selectClass = 'rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm';
const numberClass = 'w-20 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1 text-sm';

/** Assigns library samples to drum tracks. The sequencer steps stay exactly as they are. */
export function SampleInstruments() {
  const { project } = useProject();
  const { selection } = useServices();
  const selected = useObservable(selection);
  const { apply, error } = useOpRunner();
  const assets = project.assets.filter((a) => a.kind === 'audio');
  const selectedAsset = assets.find((a) => a.id === selected.sampleId);

  return (
    <Panel title="Sample instruments">
      <p className="mb-3 text-xs text-zinc-400">
        A drum track plays its sample instead of the built-in synth; the pattern steps, velocities and mixer settings stay the same. Pitch changes playback speed
        (shorter and higher, or longer and lower). Tracks in the same choke group cut each other off (e.g. open and closed hats).
      </p>
      {error && (
        <div className="mb-3">
          <Alert title={error} />
        </div>
      )}
      <ul className="flex flex-col gap-2" aria-label="Drum tracks">
        {DRUM_INSTRUMENTS.map((instrument) => {
          const track = project.tracks.find((t) => t.instrument === instrument);
          if (!track) return null;
          return (
            <li key={instrument} className="rounded-md border border-zinc-800 bg-zinc-800/40 p-2.5" data-testid={`sample-slot-${instrument}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-20 text-sm font-medium">{INSTRUMENT_LABELS[instrument]}</span>
                <label htmlFor={`slot-asset-${instrument}`} className="sr-only">
                  Sound for {INSTRUMENT_LABELS[instrument]}
                </label>
                <select
                  id={`slot-asset-${instrument}`}
                  value={track.sample?.assetId ?? ''}
                  onChange={(e) =>
                    apply((p) => (e.target.value ? audioOps.assignSample(p, instrument, e.target.value) : audioOps.unassignSample(p, instrument)))
                  }
                  className={`${selectClass} min-w-40 flex-1 sm:flex-none`}
                >
                  <option value="">Built-in synth</option>
                  {assets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
                {selectedAsset && track.sample?.assetId !== selectedAsset.id && (
                  <Button size="sm" onClick={() => apply((p) => audioOps.assignSample(p, instrument, selectedAsset.id))}>
                    Use selected<span className="sr-only"> sample on {INSTRUMENT_LABELS[instrument]}</span>
                  </Button>
                )}
              </div>
              {track.sample && <SlotControls instrument={instrument} slot={track.sample} apply={apply} durationSec={assets.find((a) => a.id === track.sample!.assetId)?.audio?.durationSec} />}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

function SlotControls({
  instrument,
  slot,
  apply,
  durationSec,
}: {
  instrument: Instrument;
  slot: SampleSlot;
  apply: ReturnType<typeof useOpRunner>['apply'];
  durationSec: number | undefined;
}) {
  const id = (k: string) => `slot-${k}-${instrument}`;
  const label = INSTRUMENT_LABELS[instrument];
  const set = (patch: Partial<Omit<SampleSlot, 'assetId'>>) => apply((p) => audioOps.updateSampleSlot(p, instrument, patch));
  const num = (v: string) => (v.trim() === '' ? undefined : Number(v));
  return (
    <div className="mt-2 flex flex-wrap items-end gap-3 text-xs text-zinc-300">
      <div className="flex flex-col gap-1">
        <label htmlFor={id('gain')}>Gain {slot.gainDb} dB</label>
        <input id={id('gain')} type="range" min={-24} max={12} step={0.5} value={slot.gainDb} onChange={(e) => set({ gainDb: Number(e.target.value) })} />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={id('pitch')}>Pitch (semitones)</label>
        <input id={id('pitch')} type="number" min={-24} max={24} step={1} value={slot.pitchSemitones} onChange={(e) => num(e.target.value) !== undefined && set({ pitchSemitones: num(e.target.value)! })} className={numberClass} />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={id('start')}>Trim start (s)</label>
        <input id={id('start')} type="number" min={0} max={durationSec} step={0.01} value={slot.trimStartSec} onChange={(e) => num(e.target.value) !== undefined && set({ trimStartSec: num(e.target.value)! })} className={numberClass} />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={id('end')}>Trim end (s)</label>
        <input
          id={id('end')}
          type="number"
          min={0}
          max={durationSec}
          step={0.01}
          placeholder="end"
          value={slot.trimEndSec ?? ''}
          onChange={(e) => set({ trimEndSec: num(e.target.value) ?? null })}
          className={numberClass}
        />
      </div>
      <label className="flex items-center gap-1.5 pb-1">
        <input type="checkbox" checked={slot.reverse} onChange={(e) => set({ reverse: e.target.checked })} />
        Reverse
      </label>
      <div className="flex flex-col gap-1">
        <label htmlFor={id('choke')}>Choke group</label>
        <select id={id('choke')} value={slot.chokeGroup} onChange={(e) => set({ chokeGroup: Number(e.target.value) })} className={selectClass}>
          <option value={0}>None</option>
          {Array.from({ length: CHOKE_GROUPS }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              Group {i + 1}
            </option>
          ))}
        </select>
      </div>
      <Button size="sm" variant="ghost" onClick={() => apply((p) => audioOps.unassignSample(p, instrument))}>
        Use built-in<span className="sr-only"> {label} sound</span>
      </Button>
    </div>
  );
}
